import React, { useEffect, useRef } from "react";
import { Form, Input, InputRef, Modal, Radio, Space } from "antd";

// What to do with the nested content of the entity being duplicated:
// 'duplicate' → independent copies; 'link' → the copy shares the SAME
// children with the original (only the join rows are created).
export type DuplicationChildrenMode = "duplicate" | "link";

export interface DuplicateFormValues {
  title: string;
  children_mode: DuplicationChildrenMode;
  program_code?: string;
}

interface DuplicateModalProps {
  open: boolean;
  /** Entity name shown in the title, e.g. "curso", "lección", "programa". */
  entityLabel: string;
  /** Title of the record being duplicated; prefills the name input as-is. */
  originalTitle: string;
  /**
   * Noun phrase for the nested content the children_mode applies to, e.g.
   * "los cursos, proyectos y recursos del programa".
   */
  childrenLabel: string;
  /** Programs only: show the required program_code field (unique per program). */
  showProgramCode?: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (values: DuplicateFormValues) => void;
}

/**
 * Asks for the name of the copy and what to do with its nested content
 * (duplicate vs. link) before duplicating a course, lesson or program. The
 * confirm handler owns the mutation; this component only collects and
 * validates the form values.
 */
const DuplicateModal: React.FC<DuplicateModalProps> = ({
  open,
  entityLabel,
  originalTitle,
  childrenLabel,
  showProgramCode = false,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [form] = Form.useForm<DuplicateFormValues>();
  const titleInputRef = useRef<InputRef>(null);

  // initialValues only applies on mount, so the prefill must be reset every
  // time the modal opens for a different record. The prefill is the original
  // name as-is (no "Copia de" prefix): variant names go at the END
  // ("... Script", "... 2027"), so the cursor lands there ready to type the
  // suffix. The timeout waits for the Modal to mount its content.
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      title: originalTitle,
      children_mode: "duplicate",
      program_code: undefined,
    });
    const focusTimer = setTimeout(
      () => titleInputRef.current?.focus({ cursor: "end" }),
      100,
    );
    return () => clearTimeout(focusTimer);
  }, [open, originalTitle, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onConfirm({
        title: values.title.trim(),
        children_mode: values.children_mode,
        program_code: values.program_code?.trim() || undefined,
      });
    } catch {
      // Validation errors are already rendered inline by the form.
    }
  };

  return (
    <Modal
      title={`Duplicar ${entityLabel}`}
      open={open}
      okText="Duplicar"
      cancelText="Cancelar"
      confirmLoading={loading}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <p>
        Se creará una copia de <strong>{originalTitle}</strong>. El original no
        se modifica.
      </p>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="title"
          label="Nombre de la copia"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Escribe el nombre de la copia",
            },
          ]}
        >
          <Input
            ref={titleInputRef}
            placeholder="Nombre de la copia"
            maxLength={256}
          />
        </Form.Item>
        <Form.Item
          name="children_mode"
          label={`¿Qué hacer con ${childrenLabel}?`}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="duplicate">
                Duplicar: la copia recibe copias independientes; editarlas
                nunca afecta al original.
              </Radio>
              <Radio value="link">
                Asociar los existentes: la copia usa el mismo contenido que el
                original; editarlo afecta a ambos.
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        {showProgramCode && (
          <Form.Item
            name="program_code"
            label="Código de programa"
            tooltip="El código es único por programa; la copia no puede heredar el del original."
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Escribe el código del programa nuevo",
              },
            ]}
          >
            <Input placeholder="Ej: PRI-3-2027" maxLength={256} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default DuplicateModal;
